import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Chip,
  Typography,
  TextField,
  Button,
  Tooltip,
  CircularProgress,
  Drawer,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Phone,
  NoteAdd,
  CalendarMonth,
  Close,
  Search,
  FilterList,
  Edit,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useApi } from '../../contexts/ApiContext';
import { format } from 'date-fns';

/**
 * Tab de Seguimiento - Llamadas marcadas para seguimiento
 * Permite agregar notas, agendar callbacks, y recuperar llamadas
 */
const CallTrackingTab = ({ agentId, onRecoverCall, onScheduleCall }) => {
  const { agentTracking, agentTags } = useApi();

  const [trackingCalls, setTrackingCalls] = useState([]);
  const [tagColors, setTagColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [editingClient, setEditingClient] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    status: 'active',
    tags: ''
  });

  // Estados para notas
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editName, setEditName] = useState('');
  const [editDocument, setEditDocument] = useState('');
  const [editEmail, setEditEmail] = useState('');

  // Estados para agendamiento
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');

  // Cargar colores de etiquetas del agente
  const loadAgentTags = async () => {
    if (!agentId) return;

    try {
      const result = await agentTags.getAll(agentId);
      if (result.success) {
        const colorMap = {};
        (result.data || []).forEach((tag) => {
          colorMap[tag.tag_name] = tag.tag_color;
        });
        setTagColors(colorMap);
      }
    } catch (error) {
      console.error('[TRACKING] Error cargando etiquetas del agente:', error);
    }
  };

  useEffect(() => {
    loadAgentTags();
  }, [agentId]);

  const getTagColor = (tags = []) => {
    for (const tag of tags) {
      if (tagColors[tag]) return tagColors[tag];
    }
    return '#f5f7fa';
  };

  const getContrastText = (hexColor) => {
    const hex = hexColor?.replace('#', '');
    if (!hex || hex.length !== 6) return '#111';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#111' : '#fff';
  };

  // Cargar llamadas en seguimiento
  const loadTracking = async () => {
    try {
      setLoading(true);

      const params = {
        status: filters.status,
        page: 1,
        limit: 100
      };

      if (filters.search) params.search = filters.search;
      if (filters.tags) params.tags = filters.tags;

      const response = await agentTracking.getAgentTracking(agentId, params);

      if (response.success) {
        setTrackingCalls(response.data || []);
      }
    } catch (error) {
      console.error('[TRACKING] Error cargando seguimiento:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (agentId) {
      loadTracking();
    }
  }, [agentId, filters]);

  // Seleccionar llamada y abrir drawer
  const handleSelectCall = async (call) => {
    setSelectedCall(call);
    setDrawerOpen(true);
    try {
      const response = await agentTracking.getTrackingById(call.id);
      if (response.success) {
        setSelectedCall(response.data);
      }
    } catch (error) {
      console.error('[TRACKING] Error cargando detalle de tracking:', error);
    }
  };

  // Cerrar drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedCall(null);
    setNewNote('');
    setEditingNote(null);
  };

  // Agregar nota
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedCall) return;

    try {
      setAddingNote(true);

      await agentTracking.addNote(selectedCall.id, {
        agent_id: agentId,
        note_text: newNote.trim(),
        note_type: 'general'
      });

      setNewNote('');
      await loadTracking();

      // Recargar detalles de la llamada seleccionada
      const response = await agentTracking.getTrackingById(selectedCall.id);
      if (response.success) {
        setSelectedCall(response.data);
      }
    } catch (error) {
      console.error('[TRACKING] Error agregando nota:', error);
    } finally {
      setAddingNote(false);
    }
  };

  // Editar nota
  const handleEditNote = async (noteId) => {
    if (!editNoteText.trim()) return;

    try {
      await agentTracking.updateNote(noteId, {
        note_text: editNoteText.trim()
      });

      setEditingNote(null);
      setEditNoteText('');
      await loadTracking();

      // Recargar detalles
      const response = await agentTracking.getTrackingById(selectedCall.id);
      if (response.success) {
        setSelectedCall(response.data);
      }
    } catch (error) {
      console.error('[TRACKING] Error editando nota:', error);
    }
  };

  // Eliminar nota
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;

    try {
      await agentTracking.deleteNote(noteId);
      await loadTracking();

      // Recargar detalles
      const response = await agentTracking.getTrackingById(selectedCall.id);
      if (response.success) {
        setSelectedCall(response.data);
      }
    } catch (error) {
      console.error('[TRACKING] Error eliminando nota:', error);
    }
  };

  // Abrir diálogo de agendamiento
  const handleOpenScheduleDialog = () => {
    setScheduleDialogOpen(true);
    // Fecha mínima: mañana
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    setScheduleDate(tomorrow.toISOString().slice(0, 16));
  };

  // Crear agendamiento
  const handleCreateSchedule = async () => {
    if (!scheduleDate || !selectedCall) return;

    try {
      await agentTracking.createSchedule(selectedCall.id, {
        agent_id: agentId,
        scheduled_date: scheduleDate,
        schedule_note: scheduleNote.trim() || null
      });

      setScheduleDialogOpen(false);
      setScheduleDate('');
      setScheduleNote('');

      alert('Callback agendado exitosamente');

      // Recargar detalles
      const response = await agentTracking.getTrackingById(selectedCall.id);
      if (response.success) {
        setSelectedCall(response.data);
      }
    } catch (error) {
      console.error('[TRACKING] Error agendando callback:', error);
      alert('Error al agendar callback');
    }
  };

  // Cambiar estado del tracking
  const handleChangeStatus = async (trackingId, newStatus) => {
    try {
      await agentTracking.updateStatus(trackingId, newStatus);
      await loadTracking();

      if (selectedCall && selectedCall.id === trackingId) {
        handleCloseDrawer();
      }
    } catch (error) {
      console.error('[TRACKING] Error cambiando estado:', error);
    }
  };

  // Guardar datos de cliente
  const handleSaveClientData = async () => {
    if (!selectedCall) return;
    try {
      setSavingClient(true);
      await agentTracking.updateClientData(selectedCall.id, {
        client_name: editName.trim() || null,
        client_document: editDocument.trim() || null,
        client_email: editEmail.trim() || null
      });

      const detail = await agentTracking.getTrackingById(selectedCall.id);
      if (detail.success) {
        setSelectedCall(detail.data);
      }
      await loadTracking();
    } catch (error) {
      console.error('[TRACKING] Error actualizando datos del cliente:', error);
      alert('Error al guardar los datos del cliente');
    } finally {
      setSavingClient(false);
    }
  };

  useEffect(() => {
    if (selectedCall) {
      setEditName(selectedCall.client_name || '');
      setEditDocument(selectedCall.client_document || '');
      setEditEmail(selectedCall.client_email || '');
      setEditingClient(false);
    }
  }, [selectedCall]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Filtros */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar cliente, teléfono..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={filters.status}
              label="Estado"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="completed">Completados</MenuItem>
              <MenuItem value="cancelled">Cancelados</MenuItem>
            </Select>
          </FormControl>

          {(filters.search || filters.tags) && (
            <Chip
              icon={<FilterList />}
              label="Filtros activos"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Lista de llamadas en seguimiento */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : trackingCalls.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No hay llamadas en seguimiento
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {trackingCalls.map((call) => {
              const primaryColor = getTagColor(call.tags);
              const accentColor = primaryColor;
              const textColor = '#0f172a';
              const secondaryText = '#475569';
              const gradientBg = `linear-gradient(130deg, #ffffff 0%, ${alpha(accentColor, 0.08)} 100%)`;

              return (
              <ListItem
                key={call.id}
                disablePadding
                sx={{
                  borderBottom: '1px solid transparent',
                  px: 2,
                  py: 0.5
                }}
              >
                <ListItemButton
                  selected={selectedCall?.id === call.id}
                  onClick={() => handleSelectCall(call)}
                  sx={{
                    position: 'relative',
                    py: 1.6,
                    px: 2.25,
                    alignItems: 'flex-start',
                    borderRadius: 1.5,
                    color: textColor,
                    border: '1px solid #e5e7eb',
                    background: gradientBg,
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                    overflow: 'hidden',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 6,
                      bgcolor: accentColor,
                      opacity: 0.95
                    },
                    '&:hover': {
                      transform: 'translateY(-1.5px)',
                      boxShadow: `0 14px 34px ${alpha('#0f172a', 0.12)}`,
                      borderColor: alpha(accentColor, 0.35)
                    },
                    '&.Mui-selected': {
                      borderColor: alpha(accentColor, 0.6),
                      boxShadow: `0 16px 38px ${alpha(accentColor, 0.28)}`,
                      background: `linear-gradient(130deg, #ffffff 0%, ${alpha(accentColor, 0.12)} 100%)`
                    }
                  }}
                >
                  <ListItemText
                    primaryTypographyProps={{ sx: { fontWeight: 700, color: textColor, letterSpacing: '0.02em' } }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 800 }}>
                            {call.client_name || call.phone_number}
                          </Typography>
                          {call.total_interactions > 1 && (
                            <Chip
                              label={`${call.total_interactions} contactos`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: alpha(accentColor, 0.18),
                                color: textColor,
                                border: 'none'
                              }}
                            />
                          )}
                        </Box>
                        {onRecoverCall && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRecoverCall(call);
                          }}
                          sx={{
                            color: '#0b5cff',
                            bgcolor: '#e8f0ff',
                            border: '1px solid #d6e4ff',
                            '&:hover': { bgcolor: '#dce9ff' }
                          }}
                        >
                          <Phone fontSize="small" />
                        </IconButton>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" sx={{ color: secondaryText }} display="block">
                          {call.phone_number} • {call.client_document || 'Sin documento'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#0f172a', 0.6) }} display="block">
                          {format(new Date(call.created_at), 'dd/MM/yyyy HH:mm')} • {call.original_skill || 'Sin skill'}
                        </Typography>
                        <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {call.tags?.map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: alpha(tagColors[tag] || accentColor, 0.18),
                                color: '#0f172a',
                                border: `1px solid ${alpha(tagColors[tag] || accentColor, 0.35)}`
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Drawer de detalles */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: 400 }
        }}
      >
        {selectedCall && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedCall.client_name || 'Sin nombre'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCall.phone_number}
                  </Typography>
                  {selectedCall.client_document && (
                    <Typography variant="caption" color="text.secondary">
                      Doc: {selectedCall.client_document}
                    </Typography>
                  )}
                </Box>
                <IconButton onClick={handleCloseDrawer} size="small">
                  <Close />
                </IconButton>
              </Box>

              <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {selectedCall.tags?.map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={tag}
                    size="small"
                    sx={{
                      bgcolor: alpha(tagColors[tag] || '#e0e0e0', 0.2),
                      color: '#0f172a',
                      fontWeight: 700,
                      border: `1px solid ${alpha(tagColors[tag] || '#0f172a', 0.25)}`
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ mt: 1.5, display: 'grid', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Datos del cliente
                  </Typography>
                  {!editingClient && (
                    <Button size="small" variant="text" onClick={() => setEditingClient(true)}>
                      Editar
                    </Button>
                  )}
                </Box>

                {!editingClient ? (
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    <Typography variant="body2"><strong>Nombre:</strong> {selectedCall.client_name || 'Sin nombre'}</Typography>
                    <Typography variant="body2"><strong>Documento:</strong> {selectedCall.client_document || 'Sin documento'}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {selectedCall.client_email || 'Sin email'}</Typography>
                  </Box>
                ) : (
                  <>
                    <TextField
                      size="small"
                      label="Nombre"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Documento"
                      value={editDocument}
                      onChange={(e) => setEditDocument(e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      type="email"
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEditName(selectedCall.client_name || '');
                          setEditDocument(selectedCall.client_document || '');
                          setEditEmail(selectedCall.client_email || '');
                          setEditingClient(false);
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          await handleSaveClientData();
                          setEditingClient(false);
                        }}
                        disabled={savingClient}
                        fullWidth
                      >
                        {savingClient ? 'Guardando...' : 'Guardar'}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>

              <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                {onRecoverCall && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Phone />}
                  onClick={() => onRecoverCall(selectedCall)}
                  fullWidth
                >
                  Llamar
                </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CalendarMonth />}
                  onClick={handleOpenScheduleDialog}
                  fullWidth
                >
                  Agendar
                </Button>
              </Box>
            </Box>

            {/* Notas */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Notas
              </Typography>

              {selectedCall.notes && selectedCall.notes.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  {selectedCall.notes.map((note) => (
                    <Box
                      key={note.id}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        bgcolor: '#f9f9f9',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {editingNote === note.id ? (
                        <Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            value={editNoteText}
                            onChange={(e) => setEditNoteText(e.target.value)}
                            autoFocus
                          />
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button size="small" onClick={() => setEditingNote(null)}>
                              Cancelar
                            </Button>
                            <Button size="small" variant="contained" onClick={() => handleEditNote(note.id)}>
                              Guardar
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2">{note.note_text}</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(note.created_at), 'dd/MM HH:mm')}
                            </Typography>
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingNote(note.id);
                                  setEditNoteText(note.note_text);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteNote(note.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  No hay notas aún
                </Typography>
              )}

              {/* Agregar nota */}
              <Divider sx={{ my: 2 }} />
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                label="Nueva nota"
                placeholder="Escribe una nota..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={addingNote}
              />
              <Button
                variant="contained"
                startIcon={addingNote ? <CircularProgress size={16} /> : <NoteAdd />}
                onClick={handleAddNote}
                disabled={!newNote.trim() || addingNote}
                fullWidth
                sx={{ mt: 1 }}
              >
                {addingNote ? 'Agregando...' : 'Agregar Nota'}
              </Button>
            </Box>

            {/* Footer */}
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleChangeStatus(selectedCall.id, 'completed')}
                fullWidth
              >
                Marcar como Completado
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Diálogo de agendamiento */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agendar Callback</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Fecha y hora"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Nota (opcional)"
              placeholder="Motivo del callback..."
              value={scheduleNote}
              onChange={(e) => setScheduleNote(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreateSchedule} variant="contained" disabled={!scheduleDate}>
            Agendar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallTrackingTab;
